import { subject } from '@casl/ability'
import { sizes } from '@lmc/common/constants'
import type { RouterProps } from '@lmc/common/types'
import { Banner, Button as DsButton, EditIcon } from '@lmc/design-system'
import type {
  Carer,
  GoalListResponse,
  Home,
  Log,
  LogCategory,
  LogCategoryItem,
  Resident
} from '@lmc/ts-common'
import { Button, EmptyState, NotFoundIcon, PageHeader } from '@lmc/ui-kit'
import { useRequestedHomeSwitch } from '@shared/hooks/useRequestedHomeSwitch'
import { Alert } from 'antd'
import type { RoleAbilityProp } from 'components/AccessControl'
import HeaderIconContainer from 'components/generic/HeaderIconContainer'
import useIncidentReportByLog from 'hooks/useIncidentReport/useIncidentReportByLog'
import { find } from 'lodash'
import type React from 'react'
import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  connect,
  type MapDispatchToPropsParam,
  type MapStateToPropsParam
} from 'react-redux'
import { Link } from 'react-router-dom'
import useGoals from 'screens/Residents/screens/ResidentsGoalsScreen/components/useGoals/useGoals'
import Selectors from 'selectors'
import styled from 'styled-components'
import Actions from '../../../actions'
import { getJSON } from '../../../common/services/api'
import { Spinner } from '../../../components/generic'
import { BODY_MAPS_CATEGORY } from '../../../components/modals/customEntryVisibility'
import { EDIT_LOG_DETAILS_MODAL_ID } from '../../../components/modals/EditLogDetailsModal/modalId'
import { EDIT_STRUCTURED_LOG_MODAL_ID } from '../../../components/modals/EditStructuredLogModal/modalId'
import Operations from '../../../operations'
import { history } from '../../../store'
import { commonPatterns } from '../../../utils/enhancedTestIds'
import { withRouter } from '../../../withRouter'
import IncidentLogScreen from '../components/incidents/IncidentReportScreen'
import IncidentReportScreenV2 from '../components/incidents/IncidentReportScreenV2'
import LogDetails from '../components/log/LogDetails'
import LogDetailsV2 from '../components/log/LogDetailsV2'
import _LogEditForm from '../components/log/LogEditForm'
import useStructuredLogsFlag from '../questionnaire/useStructuredLogsFlag'

interface LogWithRevisions extends Log {
  logRevisions?: Log[]
  externalId?: string
}

interface StateProps {
  loggedBy: Carer
  editedBy: Carer
  witnessedBy: Carer
  resident: Resident
  item: LogCategoryItem
  category: LogCategory
  features: Home
  log: any // LogWithRevisions | undefined
  hasAccess?: any
  testId?: string
}

interface DispatchProps {
  loadLog: () => Promise<void>
  loadPainChekData: (id: string) => Promise<void>
  updateListItem: (opts: {
    listId: string
    itemId: string
    json: { deleted: boolean }
    successMsg?: string
  }) => Promise<unknown> | undefined
  openStructuredEdit: (log: LogWithRevisions) => void
  openDetailsEdit: (log: LogWithRevisions) => void
  router: RouterProps
}

// PainChekAssessment is in the latest version of TS-Common
interface IPainChekAssessment {
  uuid?: string
  patient?: string
  json_version?: string
  data?: {
    assessment?: {
      date?: string
      mode?: string
      pain_score?: number
      pain_level?: string
      is_editable?: boolean
      timing?: string
      comment?: string
      body_location?: string
    }
    device?: {
      manufacturer?: string
      model?: string
      os_type?: string
      os_version?: string
      app_version?: string
    }
    face?: {
      mode?: string
      initial_mode?: string
      camera?: string
      capture_seconds?: number
      frames?: number
      frame_errors?: number
      comment?: string
      score?: number
      descriptors?: {
        brow_lowering?: boolean
        cheek_raising?: boolean
        tightening_eyelids?: boolean
        wrinkling_nose?: boolean
        raising_upper_lip?: boolean
        pulling_corner_lip?: boolean
        mouth_stretch?: boolean
        parting_lips?: boolean
        closing_eyes?: boolean
      }
    }
    voice?: {
      voice_recording?: string
      comment?: string
      score?: number
      descriptors?: {
        noisy_sounds?: boolean
        requesting_help?: boolean
        groaning?: boolean
        moaning?: boolean
        crying?: boolean
        screaming?: boolean
        loudtalk?: boolean
        howling?: boolean
        sighing?: boolean
      }
    }
    movement?: {
      comment?: string
      score?: number
      descriptors?: {
        altered_random_movement?: boolean
        restlessness?: boolean
        freezing?: boolean
        guarding_touching?: boolean
        moving_away?: boolean
        abnormal_movement?: boolean
        pacing_wandering?: boolean
      }
    }
    behaviour?: {
      comment?: string
      score?: number
      descriptors?: {
        introvert?: boolean
        verbally_offensive?: boolean
        aggressive?: boolean
        fear_or_dislike?: boolean
        inappropriate_behaviour?: boolean
        confused?: boolean
        distressed?: boolean
      }
    }
    activity?: {
      comment?: string
      score?: number
      descriptors?: {
        resisting_care?: boolean
        prolonged_resting?: boolean
        altered_sleep?: boolean
        altered_routine?: boolean
      }
    }
    body?: {
      comment?: string
      score?: number
      descriptors?: {
        profuse_sweating?: boolean
        pale_flushed?: boolean
        feverish_cold?: boolean
        rapid_breathing?: boolean
        painful_injuries?: boolean
        painful_conditions?: boolean
      }
    }
  }
}

interface PainChekResponse {
  result: IPainChekAssessment
}

type Props = StateProps & DispatchProps

const LogEditForm = _LogEditForm as unknown as React.JSXElementConstructor<{
  log: LogWithRevisions
  onEditComplete: () => void
  logGoals?: GoalListResponse[]
  defaultGoals?: string[]
  testId?: string
}>

export const ViewLogScreen: FC<Props> = ({
  editedBy,
  witnessedBy,
  loggedBy,
  features,
  log,
  loadLog,
  resident,
  item,
  category,
  updateListItem,
  openStructuredEdit,
  openDetailsEdit,
  hasAccess,
  testId = 'log-screen'
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [painChekAssessment, setPainChekAssessment] =
    useState<IPainChekAssessment>()
  const { t } = useTranslation()

  const { shouldSwitchHome, hasAccessToRequestedHome } = useRequestedHomeSwitch(
    { cancelRedirectUrl: '/' }
  )

  const { incidentReport, isIncidentReportLoading } = useIncidentReportByLog({
    incidentLogId: log?.id,
    enabled: category?.itemId === 'incident'
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (shouldSwitchHome) {
      return
    }

    loadLog().then(() => setIsLoading(false))
  }, [loadLog, shouldSwitchHome])

  const { goals: logGoals } = useGoals(resident?.id)

  const structuredLogs = useStructuredLogsFlag()
  const editsAsQuestionnaire =
    structuredLogs.enabled &&
    Boolean(log?.response?.item?.length) &&
    category?.itemId !== BODY_MAPS_CATEGORY
  const editsDetails = structuredLogs.enabled && !editsAsQuestionnaire
  const Details = structuredLogs.enabled ? LogDetailsV2 : LogDetails
  const Header = structuredLogs.enabled ? CompactPageHeader : PageHeader

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (log?.id && log.externalId) {
      getJSON({
        url: `/api/services/painchek/assessments/${log.externalId}`
      }).then((res: PainChekResponse) => {
        setPainChekAssessment(res.result)
      })
    }
  }, [log?.externalId, log?.id])

  const hasRolePermission = hasAccess([
    {
      i: 'view',
      this: subject('Log', {
        item: incidentReport?.log.item?.id
      })
    }
  ])

  const canUpdateLog = hasAccess([
    { i: 'update', this: subject('Log', { item: log?.item }) }
  ])

  if (
    category?.itemId === 'incident' &&
    features.features?.pro?.enabled &&
    incidentReport !== undefined &&
    Object.keys(incidentReport).length > 0
  ) {
    if (structuredLogs.isLoading) return <Spinner />
    return structuredLogs.enabled ? (
      <IncidentReportScreenV2 />
    ) : (
      <IncidentLogScreen />
    )
  }

  const goBack = () => history.back()

  const handleDeleteUndo = async () => {
    await updateListItem({
      listId: 'logs',
      successMsg: t('components.logEditForm.logRestored'),
      itemId: log.id,
      json: { deleted: false }
    })
    await loadLog()
  }

  const renderDelete = () =>
    structuredLogs.enabled ? (
      <RemovedBanner>
        <Banner
          type="info"
          data-testid={`${testId}-delete-alert`}
          inlineActions={
            canUpdateLog ? (
              <DsButton
                variant="transparent"
                onClick={handleDeleteUndo}
                data-testid={commonPatterns.button(testId, 'undo')}
              >
                {t('general.buttons.undo')}
              </DsButton>
            ) : undefined
          }
        >
          {t('components.logEditForm.logRemoved')}
        </Banner>
      </RemovedBanner>
    ) : (
      <Alert
        message={t('components.logEditForm.logRemoved')}
        type="info"
        data-testid={`${testId}-delete-alert`}
        action={
          <Button
            variant="default"
            onClick={handleDeleteUndo}
            data-testid={commonPatterns.button(testId, 'undo')}
          >
            {t('general.buttons.undo')}
          </Button>
        }
      />
    )

  const renderLogDetails = () => {
    const isPainchekItem = item?.itemId === 'painchek'

    return (
      <div
        style={styles.logDetailsContainer}
        data-testid={`${testId}-details-container`}
      >
        <Header
          sticky
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <HeaderIconContainer $background={category?.color}>
                <img
                  // @ts-ignore
                  src={item?.icon || category?.icon || ''}
                  alt="icon"
                  style={styles.headerIcon}
                />
              </HeaderIconContainer>
              {log.title}
            </span>
          }
          onBack={goBack}
          testId={testId}
        >
          {log.nonEditable ? null : (
            <Button
              disabled={log.deleted}
              onClick={() =>
                editsAsQuestionnaire
                  ? openStructuredEdit(log)
                  : editsDetails
                    ? openDetailsEdit(log)
                    : setIsEditing(true)
              }
              variant="primary"
              // @ts-ignore
              disabledByPermission={!canUpdateLog || isPainchekItem}
              data-testid={commonPatterns.button(testId, 'edit')}
              {...(isPainchekItem
                ? { permissionDisabledText: 'PainChek logs are read-only' }
                : null)}
            >
              {structuredLogs.enabled && <EditIcon size={16} />}
              {t('general.buttons.edit')}
            </Button>
          )}
        </Header>
        {log.deleted && renderDelete()}
        <Details
          log={log}
          resident={resident}
          item={item}
          loggedBy={loggedBy}
          witnessedBy={witnessedBy}
          editedBy={editedBy}
          features={features}
          painChekAssessmentResult={painChekAssessment}
          testId={testId}
        />
      </div>
    )
  }

  if (hasAccessToRequestedHome === false) {
    return (
      <EmptyState
        title="You don't have access to this home's logs."
        description="If you need access, please contact a team member or administrator to be added."
        icon={<NotFoundIcon />}
      >
        <Link to="/">Go back home</Link>
      </EmptyState>
    )
  }
  if (
    isLoading ||
    isIncidentReportLoading ||
    structuredLogs.isLoading ||
    shouldSwitchHome ||
    hasAccessToRequestedHome === undefined
  ) {
    return <Spinner />
  }
  if (!log || !hasRolePermission) {
    return (
      <EmptyState
        title="We cant find what you're looking for... Error 404"
        description="Don't worry, try refreshing the page and if the problem persists contact support."
        icon={<NotFoundIcon />}
      >
        <Link to="/">Go back home</Link>
      </EmptyState>
    )
  }

  return (
    <div style={styles.wrapper} data-testid={testId}>
      {isEditing ? (
        <LogEditForm
          log={log}
          onEditComplete={() => setIsEditing(false)}
          logGoals={logGoals}
          defaultGoals={log.goals}
          testId={testId}
        />
      ) : (
        renderLogDetails()
      )}
    </div>
  )
}

const styles = {
  backLink: { padding: 0 },
  wrapper: {
    margin: 'auto',
    maxWidth: sizes.childScreen,
    padding: '20px 20px 50px'
  },
  headerIcon: { height: 20 },
  logDetailsContainer: { paddingBottom: 12 }
}

const mapStateToProps = (
  state: {
    global: {
      home: Home
      user: Carer
    }
    data: {
      logs: LogWithRevisions[]
      carers: Carer[]
      homes: Home[]
      residents: Resident[]
      'log-categories': any[]
      'log-category-items': any[]
    }
  },
  props: Props
) => {
  const log = find(state.data.logs, { id: props.router.params.id })
  return {
    log,
    features: state.global.home,
    loggedBy: find(state.data.carers, { id: log?.loggedBy }),
    editedBy: find(state.data.carers, { id: log?.editedBy }),
    witnessedBy: find(state.data.carers, { id: log?.witnessedBy }),
    resident: find(state.data.residents, { id: log?.resident }),
    category: find(state.data['log-categories'], { id: log?.category }),
    item: find(state.data['log-category-items'], { id: log?.item }),
    hasAccess: (abilities: RoleAbilityProp) =>
      // @ts-ignore
      Selectors.checkAbilityAccess(state, { abilities })
  }
}

const mapDispatchToProps = (dispatch: any, props: Props) => ({
  updateListItem: (options: {
    listId: string
    itemId: string
    json: { deleted: boolean }
    successMsg?: string
  }) => dispatch(Operations.updateListItem(options)),
  openDetailsEdit: (log: LogWithRevisions) =>
    dispatch(
      Actions.openModal({
        modalId: EDIT_LOG_DETAILS_MODAL_ID,
        data: { logId: log.id }
      })
    ),
  openStructuredEdit: (log: LogWithRevisions) => {
    dispatch(
      Actions.addForm({
        formId: EDIT_STRUCTURED_LOG_MODAL_ID,
        initial: { timeLogged: log.timeLogged, title: log.title }
      })
    )
    dispatch(
      Actions.openModal({
        modalId: EDIT_STRUCTURED_LOG_MODAL_ID,
        data: { logId: log.id }
      })
    )
  },
  loadLog: () =>
    Operations.loadListItem('logs', props.router.params.id, {
      withRevisions: true
    })(dispatch)
})

export default withRouter(
  connect<StateProps, DispatchProps, Props>(
    mapStateToProps as MapStateToPropsParam<StateProps, Props, unknown>,
    mapDispatchToProps as unknown as MapDispatchToPropsParam<
      DispatchProps,
      Props
    >
  )(ViewLogScreen)
)

const RemovedBanner = styled.div`
  margin-bottom: 16px;
`

const CompactPageHeader = styled(PageHeader)`
  && {
    ${({ theme }) => theme.mediaQueries.mobileDown} {
      align-items: flex-start;
      flex-direction: row;
      gap: 12px;

      > div:first-child {
        flex: 1;
        min-width: 0;
        text-align: left;
      }

      h1 {
        font-size: 20px;
      }
    }
  }
`
